import React, { useEffect, useState } from 'react';
import { AvKeyValue } from '@fabric-ui/components';
import { ActionButton } from '@fabric-ui/components/Table';
import { ArrowDown, Copy } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { flattenObject, iconSize } from '@fabric-ui/utils';
import { Box, Button, Collapse, Skeleton } from '@mui/material';

const defaultCollapseIcon = <ArrowDown style={{ height: 18, width: 18 }} />;
const defaultExpandIcon = <ArrowDown style={{ height: 18, width: 18, transform: 'rotate(-90deg)' }} />;
const copyIcon = <Copy style={iconSize(16)} />;

const loadingSkeleton = width => <Skeleton variant="text" height={25} width={width} />;

const getEventDisplayName = eventTimestamp => (eventTimestamp === undefined ? 'event' : new Date(eventTimestamp).toLocaleString());

interface EventProps {
  isExpanded?: boolean;
  onClick: () => void;
  timestamp: string;
  event: Record<string, any>;
  filterQuery?: string;
  key?: string;
  style?: Record<string, any>;
  enableCopy?: boolean;
}

export const Event: React.FC<EventProps> = ({
  onClick,
  timestamp,
  isExpanded = false,
  filterQuery = '',
  event,
  key,
  style = {},
  enableCopy = false,
}) => {
  const copyEvent = event =>
    ActionButton(
      'Copy Event',
      () => {
        navigator.clipboard.writeText(JSON.stringify(event));
      },
      copyIcon
    );

  return (
    <Box key={key}>
      <Box sx={{ ...flex.row, gap: 1 }}>
        <Button
          sx={[{ fontWeight: 600, gap: '6px' }, isExpanded && { color: theme => theme.palette.colors.neutrals[800] }, style]}
          onClick={onClick}>
          {isExpanded ? defaultCollapseIcon : defaultExpandIcon}
          {getEventDisplayName(timestamp)}
        </Button>
        {enableCopy && copyEvent(event)}
      </Box>
      <Collapse unmountOnExit in={isExpanded}>
        <AvKeyValue
          value={flattenObject({ obj: event })}
          limit={-1}
          highlightText={filterQuery ? [filterQuery] : []}
          orientation="vertical"
        />
      </Collapse>
    </Box>
  );
};

export interface AvEventListProps {
  events?: {
    event: Record<string, any>;
    timestamp: string;
    id: string;
  }[];
  defaultExpanded?: string;
  eventId?: string;
  filterQuery?: string;
  loading?: boolean;
}

export const AvEventList: React.FC<AvEventListProps> = ({
  events = [],
  filterQuery = '',
  loading,
  defaultExpanded = '',
  eventId = 'id',
}) => {
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    setExpanded({ [defaultExpanded]: true });
  }, [defaultExpanded]);
  if (loading) {
    return (
      <Box sx={{ ...flex.col, gap: 1.5 }}>
        {loadingSkeleton(200)}
        {loadingSkeleton(200)}
        {loadingSkeleton(200)}
      </Box>
    );
  }

  const filteredEvents = events.filter(event => JSON.stringify(event).includes(filterQuery));
  return (
    <Box sx={{ ...flex.colItemsStart, gap: 1.5 }}>
      {filteredEvents.map(({ event, timestamp, [eventId]: id }: Record<string, any>) => (
        <Event
          key={id}
          isExpanded={expanded[id]}
          onClick={() => setExpanded(prev => ({ ...prev, [id]: !prev[id] }))}
          timestamp={timestamp}
          event={event}
          filterQuery={filterQuery}
        />
      ))}
    </Box>
  );
};
